/* eslint-disable */
/**
 * A React component for handling outside clicks ( v1.2.2 )
 * https://github.com/airbnb/react-outside-click-handler
 */
import React from 'react';
import PropTypes from 'prop-types';

import { addEventListener } from 'consolidated-events';
import { values as objectValues, isEmpty } from 'lodash';

// Our changes, fallback to mouse event if touch event is not supported
const HAS_TOUCH = 'ontouchstart' in window;
const TOUCH_START = HAS_TOUCH ? 'touchstart' : 'mousedown';
const TOUCH_END = HAS_TOUCH ? 'touchend' : 'mouseup';

// css `display:` property values
const DISPLAY = {
	BLOCK: 'block',
	FLEX: 'flex',
	INLINE: 'inline',
	INLINE_BLOCK: 'inline-block'
};

/**
 * @example
 * import { OutsideClickHandler } from 'vendors/OutsideClickHandler';
 */
export class OutsideClickHandler extends React.Component {
	constructor(...args) {
		super(...args);

		this.onMouseDown = this.onMouseDown.bind(this);
		this.onMouseUp = this.onMouseUp.bind(this);
		this.setChildNodeRef = this.setChildNodeRef.bind(this);
	}

	componentDidMount() {
		const { disabled, useCapture } = this.props;

		if (!disabled) this.addMouseDownEventListener(useCapture);
	}

	UNSAFE_componentWillReceiveProps( { disabled, useCapture } ) {
		const { disabled: prevDisabled } = this.props;
		if (prevDisabled !== disabled) {
			if (disabled) {
				this.removeEventListeners();
			} else {
				this.addMouseDownEventListener(useCapture);
			}
		}
	}

	componentWillUnmount() {
		this.removeEventListeners();
	}

	// Use mousedown/mouseup to enforce that clicks remain outside the root's
	// descendant tree, even when dragged. This should also get triggered on
	// touch devices.
	onMouseDown(e) {
		const { useCapture } = this.props;

		const isDescendantOfRoot = this.childNode && this.childNode.contains(e.target);
		if (!isDescendantOfRoot) {
			this.removeMouseUp = addEventListener(
				document,
				TOUCH_END,
				this.onMouseUp,
				{ capture: useCapture },
			);
		}
	}

	// Use mousedown/mouseup to enforce that clicks remain outside the root's
	// descendant tree, even when dragged. This should also get triggered on
	// touch devices.
	onMouseUp(e) {
		const { onOutsideClick } = this.props;

		// check if target element has [data-ignore-outclick] attribute
		const isDescendantOfRoot = this.childNode && this.childNode.contains(e.target);
		const shouldIgnoreOutsideClick = !isEmpty(e.target.closest('[data-ignore-outclick]'));

		if (this.removeMouseUp) this.removeMouseUp();
		this.removeMouseUp = null;

		if (!isDescendantOfRoot && !shouldIgnoreOutsideClick) {
			onOutsideClick(e);
		}
	}

	setChildNodeRef(ref) {
		this.childNode = ref;
	}

	addMouseDownEventListener(useCapture) {
		this.removeMouseDown = addEventListener(
			document,
			TOUCH_START,
			this.onMouseDown,
			{ capture: useCapture },
		);
	}

	removeEventListeners() {
		if (this.removeMouseDown) this.removeMouseDown();
		if (this.removeMouseUp) this.removeMouseUp();
	}

	render() {
		const { children, display } = this.props;

		return (
			<div
				ref={this.setChildNodeRef}
				className={this.props.className}
				style={
					display !== DISPLAY.BLOCK && objectValues(DISPLAY).includes(display)
						? { display }
						: undefined
				}
				id={ this.props.id }
			>
				{children}
			</div>
		);
	}
}

// display name
OutsideClickHandler.displayName = 'OutsideClickHandler';

// prop types
OutsideClickHandler.propTypes = {
	children: PropTypes.node.isRequired,
	onOutsideClick: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
	useCapture: PropTypes.bool,
	display: PropTypes.oneOf(objectValues(DISPLAY)),
};

// default props
OutsideClickHandler.defaultProps = {
	disabled: false,
	className: undefined,

	// `useCapture` is set to true by default so that a `stopPropagation` in the
	// children will not prevent all outside click handlers from firing - maja
	useCapture: true,
	display: DISPLAY.BLOCK,
};
